/**
 * Mission Command Catalogue
 *
 * Single source of truth for all mission commands in OSCAR.
 * Every command has a human-readable display name, a category,
 * a complexity level (simple vs advanced), optional MAVLink mapping,
 * and parameter definitions.
 *
 * The `id` field is what gets stored in MissionCommand.commandName.
 * Legacy OSCAR_ and MAV_ identifiers are kept in `legacyNames` for
 * backward compatibility and ArduPilot import/export.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type VehicleClass =
    | 'drone'
    | 'ground_robot'
    | 'boat'
    | 'fixed_wing'
    | 'generic';

export type CommandCategory = 'navigation' | 'action' | 'condition' | 'custom';

export type CommandComplexity = 'simple' | 'advanced';

export type CommandParamDef = {
    /** Internal key used in params JSON, e.g. "holdTime" */
    key: string;
    /** Human label shown in the UI, e.g. "Hold time" */
    label: string;
    /** Unit shown next to the input, e.g. "s", "m/s", "°" */
    unit?: string;
    /** Data type */
    type: 'number' | 'boolean' | 'string' | 'select';
    /** Options for type=select */
    options?: Array<{ value: string | number; label: string }>;
    /** Default value */
    defaultValue?: number | string | boolean | null;
    /** Minimum (for number) */
    min?: number;
    /** Maximum (for number) */
    max?: number;
    /** Step increment (for number) */
    step?: number;
    /** Which MAVLink param slot this maps to (param1..param7), 1-based */
    mavlinkParamIndex?: number;
    /** Only show this param in advanced mode */
    advancedOnly?: boolean;
};

export type CommandDefinition = {
    /** Canonical identifier — stored in MissionCommand.commandName */
    id: string;
    /** Human-readable name shown in the UI */
    displayName: string;
    /** Short description / tooltip */
    description: string;
    /** Which command kind this maps to */
    category: CommandCategory;
    /** Maps to MissionCommand.kind */
    kind: 'nav' | 'do' | 'condition' | 'custom';
    /** Whether this shows in simple mode or only in advanced mode */
    complexity: CommandComplexity;
    /** MAVLink command code (null for OSCAR-only commands) */
    mavlinkCode: number | null;
    /** Legacy MAVLink name for import/export */
    mavlinkName: string | null;
    /** Legacy OSCAR_ identifier(s) for backward compat */
    legacyNames: string[];
    /** Which OSCAR MissionPoint.actionType this maps to (null if not a nav point) */
    oscarActionType: string | null;
    /** Which vehicle classes can use this command. Empty = all vehicles. */
    applicableVehicles: VehicleClass[];
    /** Whether this command requires a GPS location */
    requiresLocation: boolean;
    /** Parameter definitions for this command */
    parameterDefs: CommandParamDef[];
    /** Icon hint for the UI (ant-design icon name or emoji) */
    icon?: string;
    /** Sort order within its category for the UI */
    sortOrder: number;
    /** Whether ArduPilot firmware natively supports this command */
    ardupilotSupported: boolean;
};

export type MissionProfile = {
    vehicleClass: VehicleClass;
    firmwareType?: number | null;
    supportsAltitude: boolean;
    supportsTakeoffLand: boolean;
    supportsDocking: boolean;
    supportsReverse: boolean;
    defaultAltitudeMeters?: number | null;
    defaultAltitudeMode?: 'relative' | 'absolute' | 'terrain' | null;
    defaultCruiseSpeed?: number | null;
    defaultHoverSpeed?: number | null;
    defaultTurnRadiusMeters?: number | null;
    defaultFrame?: number | null;
    availableCommands?: string[] | null;
};

export type ObstaclePolicyMode = 'avoid' | 'assist' | 'stop';
export type ScanMode = 'manual' | 'autonomous';

export type ObstaclePolicy = {
    mode?: ObstaclePolicyMode | string | null;
    softDiversionM?: number | null;
    hardDiversionM?: number | null;
    actionToleranceM?: number | null;
    maxAvoidanceAttempts?: number | null;
    replanPeriodSec?: number | null;
    blockedTimeoutSec?: number | null;
};

export type MissionMeta = {
    firmwareType?: number | null;
    vehicleType?: number | null;
    vehicleClass?: VehicleClass | null;
    altitudeMode?: string | null;
    plannedHomePosition?: [number, number, number | null] | null;
    cruiseSpeed?: number | null;
    hoverSpeed?: number | null;
    defaultFrame?: number | null;
    importSource?: string | null;
    importFileName?: string | null;
    scanMode?: ScanMode | string | null;
    obstaclePolicy?: ObstaclePolicy | null;
    archive?: {
        previousStatus?: string | null;
        archivedAt?: string | null;
        restoredAt?: string | null;
    } | null;
};

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

export const COMMAND_CATALOGUE: CommandDefinition[] = [
    // =====================================================================
    // NAVIGATION — simple
    // =====================================================================
    {
        id: 'start',
        displayName: 'Start Mission',
        description: 'Mark the starting position of the mission',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: ['OSCAR_START'],
        oscarActionType: 'start',
        applicableVehicles: [],
        requiresLocation: true,
        parameterDefs: [],
        icon: 'PlayCircleOutlined',
        sortOrder: 1,
        ardupilotSupported: false,
    },
    {
        id: 'waypoint',
        displayName: 'Go to Waypoint',
        description: 'Navigate to a GPS coordinate',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: 16,
        mavlinkName: 'MAV_CMD_NAV_WAYPOINT',
        legacyNames: ['OSCAR_MOVE', 'MAV_CMD_NAV_WAYPOINT'],
        oscarActionType: 'move',
        applicableVehicles: [],
        requiresLocation: true,
        parameterDefs: [
            {
                key: 'holdTime',
                label: 'Hold time',
                unit: 's',
                type: 'number',
                defaultValue: 0,
                min: 0,
                mavlinkParamIndex: 1,
            },
            {
                key: 'acceptRadius',
                label: 'Acceptance radius',
                unit: 'm',
                type: 'number',
                defaultValue: 0,
                min: 0,
                mavlinkParamIndex: 2,
                advancedOnly: true,
            },
            {
                key: 'passThrough',
                label: 'Pass through (0=stop)',
                unit: '',
                type: 'number',
                defaultValue: 0,
                mavlinkParamIndex: 3,
                advancedOnly: true,
            },
            {
                key: 'yawAngle',
                label: 'Desired yaw angle',
                unit: '°',
                type: 'number',
                defaultValue: null,
                min: 0,
                max: 360,
                mavlinkParamIndex: 4,
                advancedOnly: true,
            },
        ],
        icon: 'AimOutlined',
        sortOrder: 2,
        ardupilotSupported: true,
    },
    {
        id: 'nav_pause',
        displayName: 'Pause at Point',
        description: 'Navigate to this GPS point and halt. The vehicle waits here while DO commands (sensor triggers) execute before resuming.',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: [],
        oscarActionType: 'measure',
        applicableVehicles: [],
        requiresLocation: true,
        parameterDefs: [
            {
                key: 'maxWaitSeconds',
                label: 'Timeout (max wait)',
                unit: 's',
                type: 'number',
                defaultValue: 120,
                min: 5,
                max: 600,
                step: 5,
            },
            {
                key: 'continueOnTimeout',
                label: 'If timeout is reached',
                type: 'select',
                options: [
                    { value: 'skip', label: 'Skip and continue to next point' },
                    { value: 'retry', label: 'Retry measurement once' },
                    { value: 'abort', label: 'Abort mission' },
                ],
                defaultValue: 'skip',
            },
        ],
        icon: 'PauseCircleOutlined',
        sortOrder: 35,
        ardupilotSupported: false,
    },
    {
        id: 'nav_continue',
        displayName: 'Continue Navigation',
        description: 'Signal that all actions at this point are complete. Resume navigation to the next point.',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: [],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [],
        icon: 'PlayCircleOutlined',
        sortOrder: 36,
        ardupilotSupported: false,
    },
    {
        id: 'trigger_camera',
        displayName: 'Trigger Camera (single shot)',
        description: 'Fire the camera shutter once to take a single photo. Maps to ArduPilot DO_DIGICAM_CONTROL.',
        category: 'action',
        kind: 'do',
        complexity: 'simple',
        mavlinkCode: 203,
        mavlinkName: 'MAV_CMD_DO_DIGICAM_CONTROL',
        legacyNames: ['MAV_CMD_DO_DIGICAM_CONTROL'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'moduleKey',
                label: 'Camera module',
                type: 'string',
                defaultValue: '',
            },
            {
                key: 'sessionControl',
                label: 'Session control',
                type: 'select',
                options: [
                    { value: 0, label: '0 — No change' },
                    { value: 1, label: '1 — Take photo' },
                ],
                defaultValue: 1,
                mavlinkParamIndex: 5,
                advancedOnly: true,
            },
        ],
        icon: 'CameraOutlined',
        sortOrder: 36,
        ardupilotSupported: true,
    },
    {
        id: 'start_continuous_capture',
        displayName: 'Start Continuous Capture',
        description: 'Begin triggering the camera at a set distance interval while moving. Maps to ArduPilot DO_SET_CAM_TRIGG_DIST.',
        category: 'action',
        kind: 'do',
        complexity: 'simple',
        mavlinkCode: 206,
        mavlinkName: 'MAV_CMD_DO_SET_CAM_TRIGG_DIST',
        legacyNames: ['MAV_CMD_DO_SET_CAM_TRIGG_DIST'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'moduleKey',
                label: 'Camera module',
                type: 'string',
                defaultValue: '',
            },
            {
                key: 'triggerDistance',
                label: 'Trigger every',
                unit: 'm',
                type: 'number',
                defaultValue: 5,
                min: 0.5,
                step: 0.5,
                mavlinkParamIndex: 1,
            },
            {
                key: 'triggerOnce',
                label: 'Trigger once immediately',
                type: 'select',
                options: [
                    { value: 0, label: 'No' },
                    { value: 1, label: 'Yes' },
                ],
                defaultValue: 0,
                mavlinkParamIndex: 2,
                advancedOnly: true,
            },
        ],
        icon: 'VideoCameraOutlined',
        sortOrder: 37,
        ardupilotSupported: true,
    },
    {
        id: 'stop_continuous_capture',
        displayName: 'Stop Continuous Capture',
        description: 'Stop distance-based camera triggering. Sends DO_SET_CAM_TRIGG_DIST with distance = 0.',
        category: 'action',
        kind: 'do',
        complexity: 'simple',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: ['OSCAR_STOP_CONTINUOUS_CAPTURE'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'moduleKey',
                label: 'Camera module',
                type: 'string',
                defaultValue: '',
            },
        ],
        icon: 'PauseCircleOutlined',
        sortOrder: 38,
        ardupilotSupported: false,
    },
    {
        id: 'takeoff',
        displayName: 'Take Off',
        description: 'Launch and climb to a specified altitude',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: 22,
        mavlinkName: 'MAV_CMD_NAV_TAKEOFF',
        legacyNames: ['MAV_CMD_NAV_TAKEOFF'],
        oscarActionType: 'start',
        applicableVehicles: ['drone', 'fixed_wing'],
        requiresLocation: true,
        parameterDefs: [
            {
                key: 'pitchAngle',
                label: 'Pitch angle',
                unit: '°',
                type: 'number',
                defaultValue: 0,
                mavlinkParamIndex: 1,
                advancedOnly: true,
            },
            {
                key: 'yawAngle',
                label: 'Yaw angle',
                unit: '°',
                type: 'number',
                defaultValue: null,
                mavlinkParamIndex: 4,
                advancedOnly: true,
            },
        ],
        icon: 'RocketOutlined',
        sortOrder: 4,
        ardupilotSupported: true,
    },
    {
        id: 'land',
        displayName: 'Land',
        description: 'Descend and land at the current or specified position',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: 21,
        mavlinkName: 'MAV_CMD_NAV_LAND',
        legacyNames: ['MAV_CMD_NAV_LAND'],
        oscarActionType: 'end',
        applicableVehicles: ['drone', 'fixed_wing'],
        requiresLocation: true,
        parameterDefs: [
            {
                key: 'abortAlt',
                label: 'Abort altitude',
                unit: 'm',
                type: 'number',
                defaultValue: 0,
                min: 0,
                mavlinkParamIndex: 1,
                advancedOnly: true,
            },
            {
                key: 'yawAngle',
                label: 'Desired yaw angle',
                unit: '°',
                type: 'number',
                defaultValue: null,
                mavlinkParamIndex: 4,
                advancedOnly: true,
            },
        ],
        icon: 'VerticalAlignBottomOutlined',
        sortOrder: 5,
        ardupilotSupported: true,
    },
    {
        id: 'return_home',
        displayName: 'Return to Home',
        description: 'Return to the home / launch position',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: 20,
        mavlinkName: 'MAV_CMD_NAV_RETURN_TO_LAUNCH',
        legacyNames: ['MAV_CMD_NAV_RETURN_TO_LAUNCH'],
        oscarActionType: 'end',
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [],
        icon: 'HomeOutlined',
        sortOrder: 6,
        ardupilotSupported: true,
    },
    {
        id: 'dock',
        displayName: 'Dock / Park',
        description: 'Navigate to a docking station and park',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: ['OSCAR_DOCK'],
        oscarActionType: 'dock',
        applicableVehicles: ['ground_robot'],
        requiresLocation: true,
        parameterDefs: [],
        icon: 'LoginOutlined',
        sortOrder: 7,
        ardupilotSupported: false,
    },
    {
        id: 'end',
        displayName: 'End Mission',
        description: 'Mark the end of the mission',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: ['OSCAR_END'],
        oscarActionType: 'end',
        applicableVehicles: [],
        requiresLocation: true,
        parameterDefs: [],
        icon: 'StopOutlined',
        sortOrder: 8,
        ardupilotSupported: false,
    },
    {
        id: 'pass_through',
        displayName: 'Pass Through (no stop)',
        description: 'Fly or drive through this point without stopping',
        category: 'navigation',
        kind: 'nav',
        complexity: 'simple',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: ['OSCAR_PASS_THROUGH'],
        oscarActionType: 'pass_through',
        applicableVehicles: [],
        requiresLocation: true,
        parameterDefs: [],
        icon: 'SwapRightOutlined',
        sortOrder: 9,
        ardupilotSupported: false,
    },

    // =====================================================================
    // NAVIGATION — advanced
    // =====================================================================
    {
        id: 'spline_waypoint',
        displayName: 'Spline Waypoint (curved)',
        description: 'Navigate through a waypoint using a spline path for smooth curves',
        category: 'navigation',
        kind: 'nav',
        complexity: 'advanced',
        mavlinkCode: 82,
        mavlinkName: 'MAV_CMD_NAV_SPLINE_WAYPOINT',
        legacyNames: ['MAV_CMD_NAV_SPLINE_WAYPOINT'],
        oscarActionType: 'move',
        applicableVehicles: ['drone'],
        requiresLocation: true,
        parameterDefs: [
            {
                key: 'holdTime',
                label: 'Hold time',
                unit: 's',
                type: 'number',
                defaultValue: 0,
                min: 0,
                mavlinkParamIndex: 1,
            },
        ],
        icon: 'BranchesOutlined',
        sortOrder: 20,
        ardupilotSupported: true,
    },
    {
        id: 'delay_at_waypoint',
        displayName: 'Delay at Waypoint',
        description: 'Navigate to a point and wait for a specified duration',
        category: 'navigation',
        kind: 'nav',
        complexity: 'advanced',
        mavlinkCode: 93,
        mavlinkName: 'MAV_CMD_NAV_DELAY',
        legacyNames: ['MAV_CMD_NAV_DELAY'],
        oscarActionType: 'wait',
        applicableVehicles: [],
        requiresLocation: true,
        parameterDefs: [
            {
                key: 'delaySeconds',
                label: 'Delay',
                unit: 's',
                type: 'number',
                defaultValue: 5,
                min: -1,
                mavlinkParamIndex: 1,
            },
            {
                key: 'hour',
                label: 'Resume hour (UTC, -1=ignore)',
                unit: '',
                type: 'number',
                defaultValue: -1,
                min: -1,
                max: 23,
                mavlinkParamIndex: 2,
                advancedOnly: true,
            },
            {
                key: 'minute',
                label: 'Resume minute (UTC, -1=ignore)',
                unit: '',
                type: 'number',
                defaultValue: -1,
                min: -1,
                max: 59,
                mavlinkParamIndex: 3,
                advancedOnly: true,
            },
            {
                key: 'second',
                label: 'Resume second (UTC, -1=ignore)',
                unit: '',
                type: 'number',
                defaultValue: -1,
                min: -1,
                max: 59,
                mavlinkParamIndex: 4,
                advancedOnly: true,
            },
        ],
        icon: 'FieldTimeOutlined',
        sortOrder: 21,
        ardupilotSupported: true,
    },

    // =====================================================================
    // CONDITION — simple
    // =====================================================================
    {
        id: 'wait',
        displayName: 'Wait / Delay',
        description: 'Pause the mission for a set number of seconds',
        category: 'condition',
        kind: 'condition',
        complexity: 'simple',
        mavlinkCode: 112,
        mavlinkName: 'MAV_CMD_CONDITION_DELAY',
        legacyNames: ['OSCAR_WAIT', 'MAV_CMD_CONDITION_DELAY'],
        oscarActionType: 'wait',
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'delaySeconds',
                label: 'Wait time',
                unit: 's',
                type: 'number',
                defaultValue: 5,
                min: 0,
                mavlinkParamIndex: 1,
            },
        ],
        icon: 'ClockCircleOutlined',
        sortOrder: 30,
        ardupilotSupported: true,
    },

    // =====================================================================
    // CONDITION — advanced
    // =====================================================================
    {
        id: 'wait_distance',
        displayName: 'Wait Until Distance',
        description: 'Hold the current position until the vehicle has travelled a specified distance',
        category: 'condition',
        kind: 'condition',
        complexity: 'advanced',
        mavlinkCode: 114,
        mavlinkName: 'MAV_CMD_CONDITION_DISTANCE',
        legacyNames: ['MAV_CMD_CONDITION_DISTANCE'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'distance',
                label: 'Distance',
                unit: 'm',
                type: 'number',
                defaultValue: 10,
                min: 0,
                mavlinkParamIndex: 1,
            },
        ],
        icon: 'ColumnWidthOutlined',
        sortOrder: 31,
        ardupilotSupported: true,
    },
    {
        id: 'set_heading',
        displayName: 'Set Heading / Yaw',
        description: 'Rotate the vehicle to face a specified heading',
        category: 'condition',
        kind: 'condition',
        complexity: 'advanced',
        mavlinkCode: 115,
        mavlinkName: 'MAV_CMD_CONDITION_YAW',
        legacyNames: ['MAV_CMD_CONDITION_YAW'],
        oscarActionType: null,
        applicableVehicles: ['drone'],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'targetAngle',
                label: 'Target angle',
                unit: '°',
                type: 'number',
                defaultValue: 0,
                min: 0,
                max: 360,
                mavlinkParamIndex: 1,
            },
            {
                key: 'angularSpeed',
                label: 'Angular speed',
                unit: '°/s',
                type: 'number',
                defaultValue: 0,
                min: 0,
                mavlinkParamIndex: 2,
                advancedOnly: true,
            },
            {
                key: 'direction',
                label: 'Direction',
                type: 'select',
                options: [
                    { value: 1, label: 'Clockwise' },
                    { value: -1, label: 'Counter-clockwise' },
                ],
                defaultValue: 1,
                mavlinkParamIndex: 3,
            },
            {
                key: 'isRelative',
                label: 'Relative to current heading',
                type: 'select',
                options: [
                    { value: 0, label: 'Absolute' },
                    { value: 1, label: 'Relative' },
                ],
                defaultValue: 0,
                mavlinkParamIndex: 4,
            },
        ],
        icon: 'CompassOutlined',
        sortOrder: 32,
        ardupilotSupported: true,
    },

    // =====================================================================
    // ACTION (DO) — simple
    // =====================================================================
    {
        id: 'change_speed',
        displayName: 'Change Speed',
        description: 'Change the target speed of the vehicle',
        category: 'action',
        kind: 'do',
        complexity: 'simple',
        mavlinkCode: 178,
        mavlinkName: 'MAV_CMD_DO_CHANGE_SPEED',
        legacyNames: ['MAV_CMD_DO_CHANGE_SPEED'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'speedType',
                label: 'Speed type',
                type: 'select',
                options: [
                    { value: 0, label: 'Airspeed' },
                    { value: 1, label: 'Ground speed' },
                    { value: 2, label: 'Climb speed' },
                    { value: 3, label: 'Descent speed' },
                ],
                defaultValue: 1,
                mavlinkParamIndex: 1,
            },
            {
                key: 'speed',
                label: 'Target speed (-1=no change)',
                unit: 'm/s',
                type: 'number',
                defaultValue: -1,
                mavlinkParamIndex: 2,
            },
            {
                key: 'throttle',
                label: 'Throttle % (-1=no change)',
                unit: '%',
                type: 'number',
                defaultValue: -1,
                min: -1,
                max: 100,
                mavlinkParamIndex: 3,
                advancedOnly: true,
            },
        ],
        icon: 'DashboardOutlined',
        sortOrder: 40,
        ardupilotSupported: true,
    },

    // =====================================================================
    // ACTION (DO) — advanced
    // =====================================================================
    {
        id: 'set_home',
        displayName: 'Set Home Position',
        description: 'Set the home position to the current location or a specified coordinate',
        category: 'action',
        kind: 'do',
        complexity: 'advanced',
        mavlinkCode: 179,
        mavlinkName: 'MAV_CMD_DO_SET_HOME',
        legacyNames: ['MAV_CMD_DO_SET_HOME'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'useCurrent',
                label: 'Use current position',
                type: 'select',
                options: [
                    { value: 1, label: 'Yes — current position' },
                    { value: 0, label: 'No — use specified coordinate' },
                ],
                defaultValue: 1,
                mavlinkParamIndex: 1,
            },
        ],
        icon: 'HomeFilled',
        sortOrder: 41,
        ardupilotSupported: true,
    },
    {
        id: 'set_servo',
        displayName: 'Set Servo Output',
        description: 'Set a servo channel to a specified PWM value',
        category: 'action',
        kind: 'do',
        complexity: 'advanced',
        mavlinkCode: 183,
        mavlinkName: 'MAV_CMD_DO_SET_SERVO',
        legacyNames: ['MAV_CMD_DO_SET_SERVO'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'servoNumber',
                label: 'Servo / channel number',
                type: 'number',
                defaultValue: 1,
                min: 1,
                max: 16,
                mavlinkParamIndex: 1,
            },
            {
                key: 'pwmValue',
                label: 'PWM value',
                unit: 'µs',
                type: 'number',
                defaultValue: 1500,
                min: 500,
                max: 2500,
                mavlinkParamIndex: 2,
            },
        ],
        icon: 'ControlOutlined',
        sortOrder: 42,
        ardupilotSupported: true,
    },
    {
        id: 'set_relay',
        displayName: 'Set Relay On/Off',
        description: 'Toggle a relay channel on or off',
        category: 'action',
        kind: 'do',
        complexity: 'advanced',
        mavlinkCode: 181,
        mavlinkName: 'MAV_CMD_DO_SET_RELAY',
        legacyNames: ['MAV_CMD_DO_SET_RELAY'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'relayNumber',
                label: 'Relay number',
                type: 'number',
                defaultValue: 0,
                min: 0,
                mavlinkParamIndex: 1,
            },
            {
                key: 'setting',
                label: 'Setting',
                type: 'select',
                options: [
                    { value: 1, label: 'On' },
                    { value: 0, label: 'Off' },
                ],
                defaultValue: 0,
                mavlinkParamIndex: 2,
            },
        ],
        icon: 'ThunderboltOutlined',
        sortOrder: 43,
        ardupilotSupported: true,
    },
    {
        id: 'jump',
        displayName: 'Jump to Step',
        description: 'Jump to a different mission step (advanced control flow)',
        category: 'action',
        kind: 'do',
        complexity: 'advanced',
        mavlinkCode: 177,
        mavlinkName: 'MAV_CMD_DO_JUMP',
        legacyNames: ['MAV_CMD_DO_JUMP'],
        oscarActionType: null,
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'targetSeq',
                label: 'Target step number',
                type: 'number',
                defaultValue: 1,
                min: 0,
                mavlinkParamIndex: 1,
            },
            {
                key: 'repeatCount',
                label: 'Repeat count (-1=unlimited)',
                type: 'number',
                defaultValue: 1,
                min: -1,
                mavlinkParamIndex: 2,
            },
        ],
        icon: 'RetweetOutlined',
        sortOrder: 44,
        ardupilotSupported: true,
    },
    {
        id: 'set_reverse',
        displayName: 'Set Reverse Direction',
        description: 'Switch the vehicle between forward and reverse driving',
        category: 'action',
        kind: 'do',
        complexity: 'advanced',
        mavlinkCode: 194,
        mavlinkName: 'MAV_CMD_DO_SET_REVERSE',
        legacyNames: ['MAV_CMD_DO_SET_REVERSE'],
        oscarActionType: null,
        applicableVehicles: ['ground_robot'],
        requiresLocation: false,
        parameterDefs: [
            {
                key: 'reverse',
                label: 'Direction',
                type: 'select',
                options: [
                    { value: 0, label: 'Forward' },
                    { value: 1, label: 'Reverse' },
                ],
                defaultValue: 0,
                mavlinkParamIndex: 1,
            },
        ],
        icon: 'SwapOutlined',
        sortOrder: 45,
        ardupilotSupported: true,
    },

    // =====================================================================
    // CUSTOM
    // =====================================================================
    {
        id: 'custom',
        displayName: 'Custom Command',
        description: 'A custom command with manually specified parameters',
        category: 'custom',
        kind: 'custom',
        complexity: 'advanced',
        mavlinkCode: null,
        mavlinkName: null,
        legacyNames: ['OSCAR_CUSTOM', 'MAV_CMD_CUSTOM'],
        oscarActionType: 'custom',
        applicableVehicles: [],
        requiresLocation: false,
        parameterDefs: [],
        icon: 'CodeOutlined',
        sortOrder: 99,
        ardupilotSupported: false,
    },
];

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

const _byId = new Map<string, CommandDefinition>();
const _byMavlinkCode = new Map<number, CommandDefinition>();
const _byLegacy = new Map<string, CommandDefinition>();

COMMAND_CATALOGUE.forEach((cmd) => {
    _byId.set(cmd.id, cmd);
    if (cmd.mavlinkCode !== null) {
        _byMavlinkCode.set(cmd.mavlinkCode, cmd);
    }
    cmd.legacyNames.forEach((name) => {
        _byLegacy.set(name, cmd);
    });
});

/** Look up a command definition by its canonical id */
export const getCommandById = (id: string): CommandDefinition | null =>
    _byId.get(id) || null;

/** Look up a command definition by MAVLink command code */
export const getCommandByMavlinkCode = (code: number): CommandDefinition | null =>
    _byMavlinkCode.get(code) || null;

/** Look up a command by any legacy name (OSCAR_ or MAV_ prefix) */
export const getCommandByLegacyName = (name: string): CommandDefinition | null =>
    _byLegacy.get(name) || null;

/**
 * Resolve a commandName that might be a new catalogue id, a legacy OSCAR_ name,
 * or a legacy MAV_ name, and return the canonical catalogue entry.
 */
export const resolveCommand = (nameOrId: string): CommandDefinition | null =>
    _byId.get(nameOrId) || _byLegacy.get(nameOrId) || null;

/**
 * Get commands filtered by complexity, vehicle class, and/or category.
 */
export const getFilteredCommands = (options?: {
    complexity?: CommandComplexity;
    vehicleClass?: VehicleClass | null;
    category?: CommandCategory;
    availableCommandIds?: string[] | null;
}): CommandDefinition[] => {
    let result = [...COMMAND_CATALOGUE];

    if (options?.complexity) {
        const maxComplexity = options.complexity;
        if (maxComplexity === 'simple') {
            result = result.filter((cmd) => cmd.complexity === 'simple');
        }
        // 'advanced' shows everything
    }

    if (options?.vehicleClass && options.vehicleClass !== 'generic') {
        const vc = options.vehicleClass;
        result = result.filter(
            (cmd) => cmd.applicableVehicles.length === 0 || cmd.applicableVehicles.includes(vc)
        );
    }

    if (options?.category) {
        result = result.filter((cmd) => cmd.category === options.category);
    }

    if (options?.availableCommandIds && options.availableCommandIds.length > 0) {
        const allowed = new Set(options.availableCommandIds);
        result = result.filter((cmd) => allowed.has(cmd.id));
    }

    return result.sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Map a legacy commandName to the new canonical id.
 * Returns the original name if no match is found.
 */
export const normalizeLegacyCommandName = (name: string): string => {
    const resolved = resolveCommand(name);
    return resolved ? resolved.id : name;
};

/**
 * Map a MAVLink command code to the canonical command id.
 * Returns a fallback string like "mavlink_178" if not in the catalogue.
 */
export const mavlinkCodeToCommandId = (code: number): string => {
    const cmd = getCommandByMavlinkCode(code);
    return cmd ? cmd.id : `mavlink_${code}`;
};

/**
 * Get the human-readable display name for any command name/id.
 */
export const getCommandDisplayName = (nameOrId: string): string => {
    const cmd = resolveCommand(nameOrId);
    return cmd ? cmd.displayName : nameOrId;
};

/**
 * Get the OSCAR actionType for a command, if applicable.
 */
export const getCommandActionType = (nameOrId: string): string | null => {
    const cmd = resolveCommand(nameOrId);
    return cmd ? cmd.oscarActionType : null;
};

/**
 * Get the display label for a command based on the active protocol.
 * oscar: just the friendly displayName
 * mavlink / ardupilot / custom: "{displayName} ({mavlinkCode})" when mavlinkCode exists
 */
export const getCommandDisplayLabel = (nameOrId: string, protocol?: string | null): string => {
    const cmd = resolveCommand(nameOrId);
    if (!cmd) return nameOrId;
    const proto = (protocol || 'oscar').toLowerCase();
    if ((proto === 'mavlink' || proto === 'ardupilot' || proto === 'custom') && cmd.mavlinkCode !== null) {
        return `${cmd.displayName} (${cmd.mavlinkCode})`;
    }
    return cmd.displayName;
};

/**
 * Get commands filtered by protocol (and optionally by complexity/vehicleClass/category).
 * mavlink: only shows commands with MAVLink command codes
 * ardupilot: only shows ardupilotSupported=true entries
 * all other protocols: same as getFilteredCommands
 */
export const getProtocolFilteredCommands = (
    protocol?: string | null,
    options?: Parameters<typeof getFilteredCommands>[0]
): CommandDefinition[] => {
    let commands = getFilteredCommands(options);
    if (protocol === 'ardupilot') {
        commands = commands.filter((cmd) => cmd.ardupilotSupported);
    } else if (protocol === 'mavlink') {
        commands = commands.filter((cmd) => cmd.mavlinkCode !== null);
    }
    return commands;
};

// ---------------------------------------------------------------------------
// Vehicle class helpers
// ---------------------------------------------------------------------------

export const VEHICLE_CLASS_OPTIONS: Array<{ value: VehicleClass; label: string; description: string }> = [
    { value: 'drone', label: 'Drone (Multirotor)', description: 'Copter-style UAV with altitude, takeoff, and landing' },
    { value: 'fixed_wing', label: 'Fixed Wing', description: 'Airplane-style UAV with altitude and runway operations' },
    { value: 'ground_robot', label: 'Ground Robot / Rover', description: 'Ground vehicle with optional docking' },
    { value: 'boat', label: 'Boat / USV', description: 'Surface watercraft' },
    { value: 'generic', label: 'Generic', description: 'Unspecified vehicle — all commands available' },
];

export const ALTITUDE_MODE_OPTIONS: Array<{ value: string; label: string; description: string }> = [
    { value: 'relative', label: 'Relative to Home', description: 'Altitude above the home / launch position' },
    { value: 'absolute', label: 'Absolute (AMSL)', description: 'Altitude above mean sea level' },
    { value: 'terrain', label: 'Terrain Following', description: 'Altitude above terrain using terrain data' },
];

export const MAVLINK_FRAME_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 0, label: '0 — Global (WGS84 absolute)' },
    { value: 3, label: '3 — Global relative to home' },
    { value: 6, label: '6 — Global (int, absolute)' },
    { value: 10, label: '10 — Global terrain alt' },
];

export const ARDUPILOT_FIRMWARE_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 3, label: 'Multirotor (ArduCopter — 3)' },
    { value: 4, label: 'Fixed Wing (ArduPlane — 4)' },
    { value: 10, label: 'Ground Vehicle (ArduRover — 10)' },
    { value: 12, label: 'Submarine / ROV (ArduSub — 12)' },
];

/**
 * Build a default MissionProfile for a given vehicle class.
 */
// Backward-compatible type aliases used by missionModels.ts
export type MissionProfileType = MissionProfile;
export type MissionMetaType = MissionMeta;

/**
 * Build Select-compatible options from filtered commands.
 * Returns { value: string; label: string; description: string }[] for ant-design Select.
 */
export const buildCommandOptions = (options?: {
    complexity?: CommandComplexity;
    vehicleClass?: VehicleClass | null;
    category?: CommandCategory;
    availableCommandIds?: string[] | null;
}): Array<{ value: string; label: string; description: string; icon?: string }> => {
    return getFilteredCommands(options).map((cmd) => ({
        value: cmd.id,
        label: cmd.displayName,
        description: cmd.description,
        icon: cmd.icon,
    }));
};

export const buildDefaultMissionProfile = (vehicleClass: VehicleClass): MissionProfile => {
    switch (vehicleClass) {
        case 'drone':
            return {
                vehicleClass: 'drone',
                firmwareType: 3,
                supportsAltitude: true,
                supportsTakeoffLand: true,
                supportsDocking: false,
                supportsReverse: false,
                defaultAltitudeMeters: 10,
                defaultAltitudeMode: 'relative',
                defaultCruiseSpeed: 5,
                defaultHoverSpeed: 2,
                defaultFrame: 3,
                availableCommands: null,
            };
        case 'fixed_wing':
            return {
                vehicleClass: 'fixed_wing',
                firmwareType: 4,
                supportsAltitude: true,
                supportsTakeoffLand: true,
                supportsDocking: false,
                supportsReverse: false,
                defaultAltitudeMeters: 50,
                defaultAltitudeMode: 'relative',
                defaultCruiseSpeed: 15,
                defaultHoverSpeed: null,
                defaultFrame: 3,
                availableCommands: null,
            };
        case 'ground_robot':
            return {
                vehicleClass: 'ground_robot',
                firmwareType: 10,
                supportsAltitude: false,
                supportsTakeoffLand: false,
                supportsDocking: true,
                supportsReverse: true,
                defaultAltitudeMeters: null,
                defaultAltitudeMode: null,
                defaultCruiseSpeed: 1,
                defaultHoverSpeed: null,
                defaultFrame: 0,
                availableCommands: null,
            };
        case 'boat':
            return {
                vehicleClass: 'boat',
                firmwareType: 10,
                supportsAltitude: false,
                supportsTakeoffLand: false,
                supportsDocking: false,
                supportsReverse: true,
                defaultAltitudeMeters: null,
                defaultAltitudeMode: null,
                defaultCruiseSpeed: 3,
                defaultHoverSpeed: null,
                defaultFrame: 0,
                availableCommands: null,
            };
        default:
            return {
                vehicleClass: 'generic',
                firmwareType: null,
                supportsAltitude: true,
                supportsTakeoffLand: true,
                supportsDocking: true,
                supportsReverse: true,
                defaultAltitudeMeters: null,
                defaultAltitudeMode: null,
                defaultCruiseSpeed: null,
                defaultHoverSpeed: null,
                defaultFrame: null,
                availableCommands: null,
            };
    }
};
